import { TFunction } from 'i18next'
import { useMemo } from 'react'
import upperFirst from 'lodash/upperFirst'

import {
  EventType,
  EventStatus,
  SubEventType,
  ParticipantStatus,
  ParticipantOptionStatus,
  Locations,
  Rooms,
  Tags,
  SubEventStatus,
  JobTitles,
  OptionType,
  AbstractStatus,
  AbstractType,
  AbstractSource,
  AbstractSelectionStatus,
  AbstractRolesType,
  Domains,
  Themes,
  Keywords,
  BlankEnum,
  SponsorshipStatus,
} from '../types'
import reactI18n from '../i18n'
import {
  getLocations,
  getTags,
  getJobTitles,
  getDomains,
  getThemes,
  getKeywords,
  getEventStatus,
  getEventType,
  getEventOrganizer,
  getAttendanceType,
  getLaboratories,
  getSessionStatus,
  getSubEventStatus,
  getOptionStatus,
  getSubEventType,
  getOptionType,
  getSponsorshipStatus,
  getSponsorshipActivity,
  getSponsorshipType,
  getParticipantStatus,
  getMembershipLevel,
  getHomeRegion,
  getThemeCategory,
  getContributorRoles,
  getSessionType,
} from '../store.slice'
import { store } from '../store'

export interface Options<T extends string | number = string | number> {
  value: T
  label: string
  color?: string
}

// TODO update useMemo state when redux store is updated

export const getLaboratoriesOptions = () => {
  const data = getLaboratories(store.getState())

  return data.map(el => ({
    ...el,
    value: el.id,
    label: el.name,
  }))
}
export const useLaboratoriesOptions = () => {
  const opts = useMemo(() => getLaboratoriesOptions(), [])
  return opts satisfies Options[]
}

export const getActivityOptions = (t = reactI18n.t) => {
  const data = getSponsorshipActivity(store.getState())

  // t('cardiaque', { ns: 'common' })
  // t('thoracique', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useActivityOptions = (t: TFunction) => {
  const opts = useMemo(() => getActivityOptions(t), [t])
  return opts
}

export const getSponsorshipStatusColor = (status: SponsorshipStatus) => {
  switch (status) {
    case SponsorshipStatus.Accepted:
      return '#009688'
    case SponsorshipStatus.Rejected:
      return '#D93F46'
    case SponsorshipStatus.Pending:
      return '#006AB3'
    case SponsorshipStatus.WaitingList:
      return '#006AB3'
    case SponsorshipStatus.LabSelected:
      return '#f69342'
    default:
      return '#676c79'
  }
}
export const getSponsorshipStatusOptions = (t = reactI18n.t) => {
  const data = getSponsorshipStatus(store.getState())

  // t('en attente', { ns: 'common' })
  // t('accepté', { ns: 'common' })
  // t('sur liste d\'attente', { ns: 'common' })
  // t('rejeté', { ns: 'common' })
  // t('sélectionné labo', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getSponsorshipStatusColor(el.value),
  }))
}
export const useSponsorshipStatusOptions = (t: TFunction) => {
  const opts = useMemo(() => getSponsorshipStatusOptions(t), [t])
  return opts
}

export const getSponsorshipTypeOptions = () => {
  const data = getSponsorshipType(store.getState())

  return data.map(el => ({
    value: el.id,
    label: el.name,
  }))
}
export const useSponsorshipTypeOptions = () => {
  const opts = useMemo(() => getSponsorshipTypeOptions(), [])
  return opts
}

export const getAbstractRolesTypeOptions = (t = reactI18n.t) =>
  [
    { value: AbstractRolesType.Speaker, label: t('Orateur', { ns: 'common' }) },
    { value: AbstractRolesType.Author, label: t('Auteur', { ns: 'common' }) },
    { value: AbstractRolesType.CoAuthor, label: t('Co-auteur(s)', { ns: 'common' }) },
  ] as const satisfies Options[]
export const useAbstractRolesTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getAbstractRolesTypeOptions(t), [t])
  return opts
}

export const getAbstractStatusOptions = (t = reactI18n.t) =>
  [
    { value: AbstractStatus.Draft, label: t('Brouillon', { ns: 'common' }), color: '#676c79' },
    { value: AbstractStatus.Submitted, label: t('Soumis', { ns: 'common' }), color: '#10557A' },
    { value: AbstractStatus.Published, label: t('Publié', { ns: 'common' }), color: '#009688' },
  ] as const satisfies (Options & { color: string })[]
export const useAbstractStatusOptions = (t: TFunction) => {
  const opts = useMemo(() => getAbstractStatusOptions(t), [t])
  return opts
}

export const getAbstractTypeOptions = (t = reactI18n.t) =>
  [
    {
      value: AbstractType.ScienceThesis,
      label: t('Thèse scientifique', { ns: 'common' }),
      color: '#89C7D2',
    },
    {
      value: AbstractType.MedicalThesis,
      label: t('Thèse médicale', { ns: 'common' }),
      color: '#0693e3',
    },
    { value: AbstractType.DesThesis, label: t('Thèse DES', { ns: 'common' }), color: '#FD6660' },
    {
      value: AbstractType.MasterThesis,
      label: t('Thèse de master', { ns: 'common' }),
      color: '#2B6366',
    },
    { value: AbstractType[''], label: '', color: '#676c79' },
  ] as const satisfies (Options & { color: string })[]
export const useAbstractTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getAbstractTypeOptions(t), [t])
  return opts
}

export const getAbstractSourceOptions = (t = reactI18n.t) =>
  [
    { value: AbstractSource.ScholarshipPrize, label: t('Bourse/Prix', { ns: 'common' }) },
    { value: AbstractSource.Databases, label: t('Bases de données', { ns: 'common' }) },
    { value: AbstractSource.Other, label: t('Autre', { ns: 'common' }) },
  ] as const satisfies Options[]
export const useAbstractSourceOptions = (t: TFunction) => {
  const opts = useMemo(() => getAbstractSourceOptions(t), [t])
  return opts
}

export const getAbstractSelectionStatusOptions = (t = reactI18n.t) =>
  [
    {
      value: AbstractSelectionStatus.ThematicSession,
      label: t('Séance thématique', { ns: 'common' }),
      color: '#10557A',
    },
    {
      value: AbstractSelectionStatus.OralPresentation,
      label: t('Présentation orale', { ns: 'common' }),
      color: '#009688',
    },
    {
      value: AbstractSelectionStatus.InteractivePoster,
      label: t('Poster interactif', { ns: 'common' }),
      color: '#76ACB5',
    },
    {
      value: AbstractSelectionStatus.DisplayPoster,
      label: t("Poster d'affichage", { ns: 'common' }),
      color: '#0693e3',
    },
    {
      value: AbstractSelectionStatus.Rejected,
      label: t('Rejeté', { ns: 'common' }),
      color: '#D93F46',
    },
    {
      value: AbstractSelectionStatus.Cancelled,
      label: t('Annulé', { ns: 'common' }),
      color: '#676c79',
    },
    { value: AbstractSelectionStatus[''], label: t('Non défini'), color: '#676c79' },
  ] as const satisfies (Options & { color: string })[]
export const useAbstractSelectionStatusOptions = (t: TFunction) => {
  const opts = useMemo(() => getAbstractSelectionStatusOptions(t), [t])
  return opts
}

export const getParticipantStatusColor = (status: ParticipantStatus) => {
  switch (status) {
    case ParticipantStatus.PendingCheckout:
      return '#f69342'
    case ParticipantStatus.Confirmed:
      return '#009688'
    case ParticipantStatus.Cancelled:
      return '#D93F46'
    case ParticipantStatus.CheckoutFailed:
      return '#D93F46'
    case ParticipantStatus.PendingPartner:
      return '#006AB3'
    default:
      return '#676c79'
  }
}
export const getParticipantStatusOptions = (t = reactI18n.t) => {
  const data = getParticipantStatus(store.getState())

  // t('paiement en attente', { ns: 'common' })
  // t('confirmé', { ns: 'common' })
  // t('annulé', { ns: 'common' })
  // t('paiement échoué', { ns: 'common' })
  // t('En attente chez partenaire', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getParticipantStatusColor(el.value),
  }))
}
export const useParticipantStatusOptions = (t: TFunction) => {
  const opts = useMemo(() => getParticipantStatusOptions(t), [t])
  return opts
}

export const getParticipantOptionStatusColor = (status: ParticipantOptionStatus) => {
  switch (status) {
    case ParticipantOptionStatus.PendingCheckout:
      return '#f69342'
    case ParticipantOptionStatus.WaitingConfirmation:
      return '#006AB3'
    case ParticipantOptionStatus.Confirmed:
      return '#009688'
    case ParticipantOptionStatus.Cancelled:
      return '#D93F46'
    case ParticipantOptionStatus.CheckoutFailed:
      return '#D93F46'
    case ParticipantOptionStatus.WaitingList:
      return '#006AB3'
    default:
      return '#676c79'
  }
}
export const getParticipantOptionStatusOptions = (t = reactI18n.t) => {
  const data = getOptionStatus(store.getState())

  // t('paiment en attente', { ns: 'common' })
  // t('en attente de confirmation', { ns: 'common' })
  // t('confirmée', { ns: 'common' })
  // t('annulée', { ns: 'common' })
  // t('paiement échoué', { ns: 'common' })
  // t("sur liste d'attente", { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getParticipantOptionStatusColor(el.value),
  }))
}
export const useParticipantOptionStatusOptions = (t: TFunction) => {
  const opts = useMemo(() => getParticipantOptionStatusOptions(t), [t])
  return opts
}

export const getMembershipLevelOptions = (t = reactI18n.t) => {
  const data = getMembershipLevel(store.getState())

  // t('associé', { ns: 'common' })
  // t('titulaire', { ns: 'common' })
  // t('honoraire', { ns: 'common' })
  // t('pas membre', { ns: 'common' })
  return data.map(el => ({
    ...el,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useMembershipLevelOptions = (t: TFunction) => {
  const opts = useMemo(() => getMembershipLevelOptions(t), [t])
  return opts
}

export const getHomeRegionOptions = (t = reactI18n.t) => {
  const data = getHomeRegion(store.getState())

  // t('Nord', { ns: 'common' })
  // t('Ouest', { ns: 'common' })
  // t('Sud', { ns: 'common' })
  return data.map(el => ({
    ...el,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useHomeRegionOptions = (t: TFunction) => {
  const opts = useMemo(() => getHomeRegionOptions(t), [t])
  return opts
}

export const getAttendanceTypeOptions = (t = reactI18n.t) => {
  const data = getAttendanceType(store.getState())

  // t('explicit', { ns: 'common' })
  // t('implicit', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useAttendanceTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getAttendanceTypeOptions(t), [t])
  return opts
}

export const getRolesTypeOptions = (t = reactI18n.t) => {
  const data = getContributorRoles(store.getState())

  // t('orateur', { ns: 'common' })
  // t('modérateur', { ns: 'common' })
  // t('responsable', { ns: 'common' })
  // t('formateur', { ns: 'common' })
  // t('encadrant', { ns: 'common' })
  // t('membre du jury', { ns: 'common' })
  // t('participant', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useRolesTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getRolesTypeOptions(t), [t])
  return opts
}

export const getEventTypeColor = (eventType: EventType): string => {
  switch (eventType) {
    case EventType.Congress:
      return '#FD6660'
    case EventType.Seminar:
      return '#2B6366'
    case EventType.Simulation:
      return '#76ACB5'
    case EventType.Osteosynthese:
      return '#D93F46'
    case EventType.Bootcamp:
      return '#0693e3'
    case EventType.Tour:
      return '#90CAF9'
    default:
      return '#676c79' // Default color if no match
  }
}
export const getEventsTypeOptions = (t = reactI18n.t) => {
  const data = getEventType(store.getState())

  // t('séminaire', { ns: 'common' })
  // t('congrès', { ns: 'common' })
  // t('simulation', { ns: 'common' })
  // t('simlife', { ns: 'common' })
  // t('ostéosynthèse', { ns: 'common' })
  // t('bootcamp', { ns: 'common' })
  // t('Rats Tour', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getEventTypeColor(el.value),
  }))
}
export const useEventsTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getEventsTypeOptions(t), [t])
  return opts
}

export const getSubEventTypeColor = (subEventType: SubEventType): string => {
  switch (subEventType) {
    case SubEventType.WelcomeSubEvent:
      return '#676c79'
    case SubEventType.BreakSubEvent:
      return '#676c79'
    case SubEventType.PresentationSubEvent:
      return '#000000'
    case SubEventType.WorkShopSubEvent:
      return '#0693e3'
    case SubEventType.HotelSubEvent:
      return '#676c79'
    case SubEventType.MealSubEvent:
      return '#676c79'
    case SubEventType.ThesisPresentationSubEvent:
      return '#10557A'
    case SubEventType.MeetingSubEvent:
      return '#000000'
    default:
      return '#676c79' // Default color if no match
  }
}
export const getSubEventsTypeOptions = (t = reactI18n.t) => {
  const data = getSubEventType(store.getState())

  // t('Accueil', { ns: 'common' })
  // t('Pause', { ns: 'common' })
  // t('Présentation', { ns: 'common' })
  // t('Atelier', { ns: 'common' })
  // t('Hébergement', { ns: 'common' })
  // t('Repas', { ns: 'common' })
  // t('Réunion', { ns: 'common' })
  // t('Soutenance', { ns: 'common' })
  return data.map(el => {
    return {
      value: el.value,
      label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
      color: getSubEventTypeColor(el.value),
    }
  })
}
export const useSubEventsTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getSubEventsTypeOptions(t), [t])
  return opts
}

export const getSessionsStatusTypeOptions = (t = reactI18n.t) => {
  const data = getSessionStatus(store.getState())

  // t('brouillon', { ns: 'common' })
  // t('confirmé', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useSessionsStatusTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getSessionsStatusTypeOptions(t), [t])
  return opts
}

export const getSessionTypeOptions = (t = reactI18n.t) => {
  const data = getSessionType(store.getState())

  // t('bootcamp basique', { ns: 'common' })
  // t('bootcamp avancé', { ns: 'common' })
  // t('bootcamp encadrant', { ns: 'common' })
  // t('Assemblée Générale', { ns: 'common' })
  // t('Conseil d\'administration', { ns: 'common' })
  // t('Séance inaugurale', { ns: 'common' })
  // t('Communications libres', { ns: 'common' })
  // t('Séance à Thème', { ns: 'common' })
  // t('Symposium', { ns: 'common' })
  // t('Séance recherche', { ns: 'common' })
  // t('Pause', { ns: 'common' })
  // t('Atelier', { ns: 'common' })
  // t('Poster', { ns: 'common' })
  // t('Exposition', { ns: 'common' })
  // t('Collège', { ns: 'common' })
  // t('Réunion', { ns: 'common' })
  // t('Partenaire', { ns: 'common' })
  // t('DPC', { ns: 'common' })
  // t('Déjeuner', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useSessionTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getSessionTypeOptions(t), [t])
  return opts
}

export const getOptionsTypeColor = (optionType: OptionType): string => {
  switch (optionType) {
    case OptionType.SessionOption:
      return '#FD6660'
    case OptionType.SubEventOption:
      return '#0693e3'
    case OptionType.TextOption:
      return '#676c79'
    case OptionType.NumberOption:
      return '#89C7D2'
    case OptionType.CheckBoxOption:
      return '#2B6366'
    default:
      return '#676c79' // Default color if no match
  }
}
export const getOptionsTypeOptions = (t = reactI18n.t) => {
  const data = getOptionType(store.getState())

  // t('Séance', { ns: 'common' })
  // t('Intervention', { ns: 'common' })
  // t('Texte', { ns: 'common' })
  // t('Nombre', { ns: 'common' })
  // t('Case à cocher', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getOptionsTypeColor(el.value),
  }))
}
export const useOptionsTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getOptionsTypeOptions(t), [t])
  return opts
}

export const getEventOrganizerOptions = () => {
  const data = getEventOrganizer(store.getState())
  return data
}
export const useEventOrganizerOptions = () => {
  const opts = useMemo(() => getEventOrganizerOptions(), [])
  return opts
}

export const getEventStatusColor = (status: EventStatus): string => {
  switch (status) {
    case EventStatus.Draft:
      return '#676c79'
    case EventStatus.Published:
      return '#10557A'
    case EventStatus.RegistrationOpen:
      return '#89C7D2'
    case EventStatus.RegistrationClosed:
      return '#676c79'
    case EventStatus.InProgress:
      return '#10557A'
    case EventStatus.Completed:
      return '#676c79'
    case EventStatus.Archived:
      return '#676c79'
    default:
      return '#676c79' // Default color if no match
  }
}
export const getEventsStatusTypeOptions = (t = reactI18n.t) => {
  const data = getEventStatus(store.getState())

  // t('brouillon', { ns: 'common' })
  // t('publié', { ns: 'common' })
  // t('inscription ouverte', { ns: 'common' })
  // t('inscription close', { ns: 'common' })
  // t('en cours', { ns: 'common' })
  // t('terminé', { ns: 'common' })
  // t('archivé', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getEventStatusColor(el.value),
  }))
}
export const useEventsStatusTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getEventsStatusTypeOptions(t), [t])
  return opts
}

export const getSubEventStatusColor = (status: SubEventStatus): string => {
  switch (status) {
    case SubEventStatus.Draft:
      return '#676c79'
    case SubEventStatus.Confirmed:
      return '#10557A'
    default:
      return '#676c79' // Default color if no match
  }
}
export const getSubEventsStatusTypeOptions = (t = reactI18n.t) => {
  const data = getSubEventStatus(store.getState())

  // t('brouillon', { ns: 'common' })
  // t('confirmé', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
    color: getSubEventStatusColor(el.value),
  }))
}
export const useSubEventsStatusTypeOptions = (t: TFunction) => {
  const opts = useMemo(() => getSubEventsStatusTypeOptions(t), [t])
  return opts
}

export const getLocationsOptions = (data?: Locations[]) => {
  const fallback = data ?? getLocations(store.getState())
  return fallback
    ? fallback.map(el => ({
        ...el,
        value: el.id,
        label: `${el.city} - ${el.name}`,
        rooms: el.rooms.map(val => ({
          ...val,
          label: `${val.name}${val.floor ? ` - ${val.floor}` : ''}${val.capacity ? ` (max ${val.capacity})` : ''}`,
          value: val.id,
        })),
      }))
    : []
}
export const useLocationDataOptions = (data?: Locations[]) => {
  const opts = useMemo(() => getLocationsOptions(data), [data])
  return opts satisfies Options[]
}

export const getRoomsOptions = (mainLocation?: number | null, data?: Locations[]) => {
  const fallback = data ?? getLocationsOptions(data)
  const findIndex = fallback.findIndex(el => el.id === mainLocation)
  if (findIndex > 0) {
    const mainLocation = fallback.splice(findIndex, 1)
    fallback.unshift(mainLocation[0])
  }

  const formatted = fallback.reduce(
    (acc, el) => {
      if (el.rooms.length) {
        el.rooms.map(room => {
          acc.push({
            ...room,
            group: el.name,
            groupId: el.id,
            value: room.id,
            label: `${room.name}${room.floor ? ` [${room.floor === 0 ? 'RdC' : `Étage ${room.floor}`}]` : ''}${room.capacity ? ` (max ${room.capacity})` : ''}`,
          })
        })
      }
      return acc
    },
    [] as (Options<number> & Rooms & { group: string; groupId: number })[]
  ) satisfies Options[]

  return formatted
}
export const useRoomsDataOptions = (mainLocation?: number | null, data?: Locations[]) => {
  const opts = useMemo(() => getRoomsOptions(mainLocation, data), [mainLocation, data])
  return opts satisfies Options[]
}

export const getTagsOptions = (data?: Tags[]) => {
  const fallback = data ?? getTags(store.getState())
  return fallback ? fallback.map(el => ({ value: el.id, label: el.name })) : []
}
export const useTagsDataOptions = (data?: Tags[]) => {
  const opts = useMemo(() => getTagsOptions(data), [data])
  return opts satisfies Options[]
}

export const getThemeCategoryOptions = (t = reactI18n.t) => {
  const data = getThemeCategory(store.getState())

  // t('cardiaque', { ns: 'common' })
  // t('congénital', { ns: 'common' })
  // t('thoracique', { ns: 'common' })
  return data.map(el => ({
    value: el.value,
    label: upperFirst(t(el.label, { ns: 'common' })) || el.label,
  }))
}
export const useThemeCategoryDataOptions = (t?: TFunction) => {
  const opts = useMemo(() => getThemeCategoryOptions(t), [t])
  return opts satisfies Options[]
}

export const getKeywordsOptions = (data?: Keywords[]) => {
  const fallback = data ?? getKeywords(store.getState())
  return (fallback ? fallback.map(el => ({ value: el.id, label: el.name })) : []) as (Options & {
    inputValue?: string
  })[]
}
export const useKeywordsDataOptions = (data?: Keywords[]) => {
  const opts = useMemo(() => getKeywordsOptions(data), [data])
  return opts satisfies Options[]
}

// Parsing may be useless here as the data is already in the correct format from API
// But it unifies the way we get the data and prevent the day format may change in the future
export const getJobTitlesOptions = (data?: JobTitles[]) => {
  const fallback = data ?? getJobTitles(store.getState())
  return (
    fallback ? fallback.map(el => ({ value: el.value, label: el.label })) : []
  ) satisfies Options[]
}
export const useJobTitlesDataOptions = (data?: JobTitles[]) => {
  const opts = useMemo(() => getJobTitlesOptions(data), [data])
  return opts satisfies Options[]
}

export const getDomainsOptions = (data?: Domains[]) => {
  const fallback = data ?? getDomains(store.getState())
  return (
    fallback ? fallback.map(el => ({ value: el.id, label: el.name })) : []
  ) satisfies Options[]
}
export const useDomainsDataOptions = (data?: Domains[]) => {
  const opts = useMemo(() => getDomainsOptions(data), [data])
  return opts satisfies Options[]
}

export const getThemesOptions = (data?: Themes[]) => {
  const fallback = data ?? getThemes(store.getState())
  return fallback.reduce(
    (acc, el) => {
      const group = fallback.find(theme => theme.path === el.path.substring(0, 4))
      acc.push({
        ...el,
        group: group?.name ?? 'NC',
        value: el.id,
        label:
          (el.depth === 2
            ? `[${reactI18n.t('Sous-thème', { ns: 'common' })}] `
            : `[${reactI18n.t('Thème', { ns: 'common' })}] `) + el.name,
      })
      return acc
    },
    [] as (Options & Themes & { group: string })[]
  ) satisfies Options[]
}
export const useThemesDataOptions = (data?: Themes[]) => {
  const opts = useMemo(() => getThemesOptions(data), [data])
  return opts satisfies Options[]
}

export const getOptionValue = <T extends Options>(options: T[], label: T['label']): T['value'] => {
  const option = options.find(opt => opt.label === label)
  return option?.value ? (option satisfies T).value : ''
}

export const getOptionLabel = <T extends Options>(options: T[], value: T['value']): T['label'] => {
  const option = options.find(opt => opt.value === value)
  return option?.value ? (option satisfies T).label : ''
}

export const getOption = <T extends Options>(
  options: T[],
  label: T['label'] | null | undefined,
  value?: T['value'] | null | BlankEnum
) => {
  const option = options.find(opt => {
    if (label !== null && label !== undefined) return opt.label === label
    if (value !== null && value !== undefined) return opt.value === value
    return false
  })
  return option ? (option satisfies T) : null
}
